// Renders an event-row array into the same HTML structure that
// _play_index_events.html produced server-side. Templates' CSS classes
// (.results, .table-scroll, .result-meta, .mp-pager, .is-goal,
// .team-colored, .watch-cell) are preserved verbatim so the existing
// stylesheet works without changes.
//
// PR C scope: omit the YouTube watch link entirely. The clock-map
// sidecars (var/clock_maps/<game_id>.json) that power /events'
// watch_offset_s aren't shipped to the static site yet. The cell stays
// in the layout so columns line up; a follow-up will add per-game
// sidecars to dist/ and resolve the offset client-side.

import type { EventRow } from "./query";
import { teamColor } from "./team-colors";

function escapeHtml(s: string | number | null | undefined): string {
  if (s === null || s === undefined) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function plural(n: number, singular: string, pluralForm?: string): string {
  return n === 1 ? singular : (pluralForm ?? `${singular}s`);
}

export interface RenderEventsOptions {
  rows: EventRow[];
  total: number;
  offset: number;
  limit: number;
  hasXg: boolean;
}

export function renderEventsTable(opts: RenderEventsOptions): string {
  // `limit` is accepted on the options bag for API symmetry with the
  // Python /events handler, but the pager logic derives page size from
  // rows.length so we don't actually need to read it here.
  const { rows, total, offset, hasXg } = opts;
  if (total === 0) {
    return '<p class="empty">No events match those filters.</p>';
  }
  const startN = offset + 1;
  const endN = offset + rows.length;

  const head = `
    <p class="result-meta">
      Showing ${rows.length} of ${total} ${plural(total, "event")}
      ${offset > 0 ? `· starting at #${startN}` : ""}.
    </p>
    <div class="table-scroll">
    <table class="results">
      <thead>
        <tr>
          <th>Date</th>
          <th>Game</th>
          <th>Per</th>
          <th>Clock</th>
          <th>Strength</th>
          <th>Team</th>
          <th>Player</th>
          <th>Event</th>
          <th>Zone</th>
          <th>Hit recip. / Drawn by</th>
          ${hasXg ? '<th class="num">xG</th>' : ""}
        </tr>
      </thead>
      <tbody>
  `;

  const body = rows
    .map((r) => {
      const xgCell = hasXg
        ? `<td class="num">${r.xg !== null ? r.xg.toFixed(2) : "—"}</td>`
        : "";
      const goalClass = r.is_goal ? "is-goal" : "";
      const goalMark = r.is_goal ? " ✓" : "";
      const color = teamColor(r.team_code);
      const trackingCell =
        r.event_type === "hit"
          ? escapeHtml(r.hit_recipient_name) || "—"
          : r.event_type === "penalty"
            ? escapeHtml(r.penalty_drawn_name) || "—"
            : "—";
      return `
        <tr class="${goalClass}">
          <td>${escapeHtml(r.date_played)}</td>
          <td><a href="/game/${r.game_id}.html">${escapeHtml(r.home_code)} vs ${escapeHtml(r.away_code)}</a></td>
          <td>${escapeHtml(r.period)}</td>
          <td>${escapeHtml(r.time_remaining)}</td>
          <td>${escapeHtml(r.strength_state) || "—"}</td>
          <td class="team-colored" style="--team-color: ${color}">${escapeHtml(r.team_code) || "—"}</td>
          <td>${escapeHtml(r.player_name) || "—"}</td>
          <td>${escapeHtml(r.event_type)}${goalMark}</td>
          <td>${escapeHtml(r.zone)}</td>
          <td>${trackingCell}</td>
          ${xgCell}
        </tr>
      `;
    })
    .join("");

  const hasPrev = offset > 0;
  const hasNext = endN < total;
  const pager =
    hasPrev || hasNext
      ? `
    <nav class="mp-pager">
      ${hasPrev ? `<button type="button" data-action="prev">← Prev</button>` : ""}
      <span class="pager-meta">${startN}–${endN} of ${total}</span>
      ${hasNext ? `<button type="button" data-action="next">Next →</button>` : ""}
    </nav>`
      : "";

  return `${head}${body}</tbody></table></div>${pager}`;
}

export interface ShotmapWrapperOptions {
  svg: string;
  total: number;
  goals: number;
  xgTotal: number;
  hasXg: boolean;
  style: "dots" | "hexbin";
  plotted: number;
}

/** Mirrors _play_index_shotmap.html (the meta line + .shotmap-wrap +
 *  legend). Series-color legend is omitted in PR C — that case only
 *  matters on the matchup page (PR D). */
export function renderShotmap(opts: ShotmapWrapperOptions): string {
  const { svg, total, goals, xgTotal, hasXg, style, plotted } = opts;
  if (total === 0) {
    return '<p class="empty">No events match those filters.</p>';
  }
  const xgLine = hasXg
    ? `<span><strong>${xgTotal.toFixed(2)}</strong> xG</span>`
    : "";
  const plottedHint =
    style === "dots"
      ? `<span class="hint">${plotted} of ${total} plotted — shots flipped to offensive end; behind-net excluded.</span>`
      : "";
  const legend =
    style === "dots"
      ? '<div class="hint">Dot size = xG. Goals opaque with a dark outline; shots translucent.</div>'
      : `<div><span class="swatch hex-lo"></span> Fewer shots</div>
         <div><span class="swatch hex-hi"></span> More shots</div>`;
  return `
    <p class="result-meta mp-tallies">
      <span><strong>${total}</strong> ${plural(total, "event")}</span>
      <span><strong>${goals}</strong> ${plural(goals, "goal")}</span>
      ${xgLine}
      ${plottedHint}
    </p>
    <div class="shotmap-wrap">
      ${svg}
      <div class="shotmap-legend">${legend}</div>
    </div>
  `;
}
